import { useState } from 'react';
import { FileText, Download, Lock, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import ScrollReveal from '@/components/ScrollReveal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface FundDocument {
  titleKey: string;
  descKey: string;
  fileName: string;
  filePath: string;
  fileSize: string;
  updatedDate: string;
  restricted: boolean;
  hasDisclaimer?: boolean;
  comingSoon?: boolean;
}

const documents: FundDocument[] = [
  {
    titleKey: 'docFundProspectus',
    descKey: 'docFundProspectusDesc',
    fileName: 'AMGMF_Investment_Prospectus_2026.pdf',
    filePath: 'public/AMGMF_Investment_Prospectus_2026.pdf',
    fileSize: '2.4 MB',
    updatedDate: 'January 2026',
    restricted: false,
    comingSoon: true,
  },
  {
    titleKey: 'docRegulatoryNotification',
    descKey: 'docRegulatoryNotificationDesc',
    fileName: 'AMGMF_Regulatory_Confirmation_2026.pdf',
    filePath: 'public/AMGMF_Regulatory_Confirmation_2026.pdf',
    fileSize: '157 KB',
    updatedDate: 'January 2026',
    restricted: false,
  },
  {
    titleKey: 'docGovernanceCharter',
    descKey: 'docGovernanceCharterDesc',
    fileName: 'AMGMF_Governance_Framework_2026.pdf',
    filePath: 'public/AMGMF_Governance_Framework_2026.pdf',
    fileSize: '1.3 MB',
    updatedDate: 'January 2026',
    restricted: false,
  },
  {
    titleKey: 'docRiskDisclosure',
    descKey: 'docRiskDisclosureDesc',
    fileName: 'AMGMF_Risk_Disclosure_2026.pdf',
    filePath: 'public/AMGMF_Risk_Disclosure_2026.pdf',
    fileSize: '270 KB',
    updatedDate: 'January 2026',
    restricted: false,
    hasDisclaimer: true,
  },
  {
    titleKey: 'docComplianceFramework',
    descKey: 'docComplianceFrameworkDesc',
    fileName: 'AMGMF_Compliance_Framework_2026.pdf',
    filePath: 'public/AMGMF_Compliance_Framework_2026.pdf',
    fileSize: '1.9 MB',
    updatedDate: 'January 2026',
    restricted: false,
    comingSoon: true,
  },
  {
    titleKey: 'docGovernanceArchitecture',
    descKey: 'docGovernanceArchitectureDesc',
    fileName: 'Fund_Governance_Architecture.pdf',
    filePath: 'public/Fund_Governance_Architecture.pdf',
    fileSize: '414 KB',
    updatedDate: 'April 2026',
    restricted: false,
  },
  {
    titleKey: 'docInvestorForms',
    descKey: 'docInvestorFormsDesc',
    fileName: 'AMGMF_Investor_Subscription_Pack_2026.pdf',
    filePath: 'restricted/AMGMF_Investor_Subscription_Pack_2026.pdf',
    fileSize: '101 KB',
    updatedDate: 'January 2026',
    restricted: true,
  },
];

const DocumentCenter = () => {
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const [restrictedModal, setRestrictedModal] = useState(false);
  const [comingSoonModal, setComingSoonModal] = useState(false);
  const home = t.home as any;

  const logDownload = async (doc: FundDocument) => {
    try {
      await supabase.from('document_downloads' as any).insert({
        user_id: user?.id || null,
        document_name: home[doc.titleKey],
        file_path: doc.filePath,
      });
    } catch {
      // silent fail for logging
    }
  };

  const getPublicUrl = (filePath: string) => {
    const { data } = supabase.storage.from('fund-documents').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleDownload = async (doc: FundDocument) => {
    if (doc.comingSoon) {
      setComingSoonModal(true);
      return;
    }
    if (doc.restricted && !user) {
      setRestrictedModal(true);
      return;
    }
    await logDownload(doc);
    const url = getPublicUrl(doc.filePath);
    window.open(url, '_blank');
  };

  const lt = (en: string, ar: string, fr: string) =>
    lang === 'ar' ? ar : lang === 'fr' ? fr : en;

  return (
    <>
      <section id="documents" className="py-28 bg-charcoal text-primary-foreground">
        <div className="container">
          <ScrollReveal className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-display text-primary-foreground mb-4">
              {home.docCenterTitle}
            </h2>
            <p className="text-primary-foreground/50 max-w-lg mx-auto mb-6">
              {home.docCenterSubtitle}
            </p>
            <div className="w-16 h-px bg-secondary mx-auto" />
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {documents.map((doc, i) => (
              <ScrollReveal key={i} delay={i * 0.06}>
                <div className={`border border-primary-foreground/10 p-8 hover:border-secondary/30 hover:-translate-y-0.5 transition-all duration-300 group h-full flex flex-col ${doc.comingSoon ? 'opacity-75' : ''}`}>
                  <div className="flex items-start justify-between mb-5">
                    <FileText className="h-7 w-7 text-secondary" strokeWidth={1.2} />
                    {doc.restricted && (
                      <Lock className="h-5 w-5 text-primary-foreground/30" strokeWidth={1.5} />
                    )}
                    {doc.comingSoon && (
                      <Clock className="h-5 w-5 text-secondary/50" strokeWidth={1.5} />
                    )}
                  </div>

                  <h3 className="font-display text-lg mb-3 text-primary-foreground">{home[doc.titleKey]}</h3>
                  <p className="text-primary-foreground/40 text-sm leading-relaxed mb-2">
                    {home[doc.descKey]}
                  </p>
                  <p className="text-primary-foreground/25 text-xs mb-1">
                    PDF | {doc.fileSize}
                  </p>
                  <p className="text-primary-foreground/25 text-xs mb-6">
                    {home.docCenterUpdated} {doc.updatedDate}
                  </p>

                  {doc.hasDisclaimer && (
                    <p className="text-secondary/60 text-xs italic mb-5 leading-relaxed">
                      {home.docCenterDisclaimer}
                    </p>
                  )}

                  <div className="mt-auto">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="inline-flex items-center gap-2 text-sm text-secondary border border-secondary/40 px-5 py-2.5 hover:bg-secondary hover:text-secondary-foreground transition-colors uppercase tracking-wider font-medium"
                    >
                      {doc.comingSoon ? (
                        <>
                          <Clock className="h-4 w-4" />
                          {lt('Coming Soon', 'قريباً', 'Bientôt')}
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          {doc.restricted ? home.docCenterRestricted : home.docCenterDownload}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Restricted access modal */}
      <Dialog open={restrictedModal} onOpenChange={setRestrictedModal}>
        <DialogContent className="bg-charcoal border-primary-foreground/10 text-primary-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-primary-foreground">
              {home.docCenterRestrictedTitle}
            </DialogTitle>
            <DialogDescription className="text-primary-foreground/50 leading-relaxed pt-2">
              {home.docCenterRestrictedDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-4">
            <Link
              to={`/${lang}/investor`}
              onClick={() => setRestrictedModal(false)}
              className="inline-flex items-center gap-2 px-7 py-3 border border-secondary text-secondary font-medium tracking-wide uppercase hover:bg-secondary hover:text-secondary-foreground transition-colors"
            >
              {home.docCenterRequestAccess}
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coming soon modal */}
      <Dialog open={comingSoonModal} onOpenChange={setComingSoonModal}>
        <DialogContent className="bg-charcoal border-secondary/20 text-primary-foreground max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-secondary/10 flex items-center justify-center">
                <Clock className="h-8 w-8 text-secondary" />
              </div>
            </div>
            <DialogTitle className="font-display text-xl text-primary-foreground text-center">
              {lt('Document Coming Soon', 'المستند قريباً', 'Document Bientôt Disponible')}
            </DialogTitle>
            <DialogDescription className="text-primary-foreground/50 leading-relaxed pt-3 text-center">
              {lt(
                'This document is currently being finalized and will be available for download shortly. Please check back soon or contact us for more information.',
                'هذا المستند قيد الإعداد النهائي وسيكون متاحاً للتحميل قريباً. يرجى التحقق لاحقاً أو التواصل معنا لمزيد من المعلومات.',
                'Ce document est en cours de finalisation et sera disponible en téléchargement prochainement. Veuillez revenir bientôt ou nous contacter pour plus d\'informations.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-4 flex justify-center">
            <button
              onClick={() => setComingSoonModal(false)}
              className="inline-flex items-center gap-2 px-7 py-3 border border-secondary text-secondary font-medium tracking-wide uppercase hover:bg-secondary hover:text-secondary-foreground transition-colors"
            >
              {lt('Understood', 'حسناً', 'Compris')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentCenter;
